import Tray, { TrayProps } from '@components/containers/tray';
import { TruncatedText } from '@components/utils/TruncatedText';
import { css, Theme } from '@emotion/react';
import styled from '@emotion/styled';
import { AdditionalProductOutboundLinkPayload } from '@enums';
import {
    AdditionalProductFragment,
    VariantFragment,
} from '@graphql/generated/graphql';
import { useCompanyConfig } from '@lib/config/hooks';
import StrapiImage from '@lib/image/strapiImage';
import { getStrapiAssetUrl } from '@lib/strapi';
import { HStack, VStack } from '@lib/uikit';
import { getFormatedPrice } from '@utils/price';
import { useRouter } from 'next/router';

import { AdditionalProductActions } from './AdditionalProductActions';
import { useVariantSelects } from './variants/hooks/useVariantSelects';
import { VariantSelects } from './variants/VariantSelects';

const imageStyle = ({ theme }: { theme: Theme }) => css`
    min-height: 45dvh;
    max-height: 60dvh;
    aspect-ratio: 1/1;
    width: 100%;
    object-fit: cover;
    border-radius: ${theme.shape.radius.default};
`;

const SStrapiImage = styled(StrapiImage)`
    ${imageStyle}
`;

const StyledImage = styled.img`
    ${imageStyle}
`;

const Title = styled.p`
    font: ${({ theme }) => theme.typography.heading3};
`;

const Description = styled(TruncatedText)`
    font: ${({ theme }) => theme.typography.subtitle6};
    font-weight: lighter;
`;

const Price = styled(Title)`
    flex-shrink: 0;
    font-weight: 500;
`;

const Footer = styled.div`
    position: sticky;
    bottom: 0;
    left: 0;
    right: 0;
    padding-block: ${({ theme }) => theme.spacing.xxs};
    background-color: ${({ theme }) => theme.palette.background.paper};
`;

interface Props extends TrayProps {
    additionalProduct: AdditionalProductFragment;
    defaultSelectedVariant?: VariantFragment;
    context: AdditionalProductOutboundLinkPayload['context'];
}

export const AdditionalProductDetailTray = ({
    additionalProduct,
    defaultSelectedVariant,
    context,
    ...trayProps
}: Props) => {
    const { additionalProduct: additionalProductRenderOptions } =
        useCompanyConfig();
    const { selects, onSelectOption, selectedVariant } = useVariantSelects({
        additionalProduct,
        defaultSelectedVariant,
    });
    const { locale } = useRouter();

    const name = selectedVariant?.Name || additionalProduct.Name;
    const price =
        (selectedVariant?.Price &&
            getFormatedPrice(selectedVariant.Price, locale!)) ||
        additionalProduct.Price;

    // Use the first image from the selected variant if available, otherwise use the main product image
    const imageToDisplay =
        selectedVariant?.ImageUrl ||
        selectedVariant?.Images?.[0] ||
        additionalProduct.ImageUrl ||
        additionalProduct.Image;

    return (
        <Tray {...trayProps}>
            <VStack spacing='sm'>
                {typeof imageToDisplay === 'string' ? (
                    <StyledImage
                        src={imageToDisplay}
                        alt={additionalProduct.Name}
                    />
                ) : (
                    <SStrapiImage
                        sizes='(max-width: 748px) 100vw, 33vw'
                        src={getStrapiAssetUrl(imageToDisplay)}
                        formats={imageToDisplay?.formats}
                        alt={additionalProduct.Name}
                    />
                )}
                <VStack spacing='xxs'>
                    <HStack alignItems='start' justifyContent='space-between'>
                        <Title>{name}</Title>
                        <Price>{price}</Price>
                    </HStack>
                    <VariantSelects
                        selects={selects}
                        onSelectOption={onSelectOption}
                    />
                    {additionalProductRenderOptions.withDescription.inDetail &&
                        additionalProduct.Description && (
                            <Description
                                text={additionalProduct.Description}
                                maxLines={2}
                            />
                        )}
                </VStack>
                <Footer>
                    <AdditionalProductActions
                        additionalProduct={additionalProduct}
                        context={context}
                        selectedVariant={selectedVariant}
                    />
                </Footer>
            </VStack>
        </Tray>
    );
};
